/**
 * Small hook + wrapper that lets any page respect the per-CMS-field
 * visibility toggles the admin sets in the CMS Fields tab.
 *
 * Usage:
 *   const vis = useCmsFieldVisibility();
 *   {vis.shouldShow("price", "user") && <PriceInput />}
 *
 * Or with the ready-made wrapper:
 *   <UserCmsField vis={vis} fieldKey="price" label="Price" required>
 *     <Input value={form.price} onChange={...} />
 *   </UserCmsField>
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import CmsFieldWrap from "@/components/admin/CmsFieldWrap";

const API = getApiUrl();

/**
 * Standalone hook — fetches CMS field settings once and exposes a
 * `shouldShow(key, audience)` helper plus the raw settings object.
 *
 * Tries the authenticated admin endpoint first and falls back to the public
 * one so the property-admin (user) dashboard, which has its own separate
 * session cookie, can still read the metadata.
 */
export function useCmsFieldVisibility() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios
          .get(`${API}/admin/cms-field-settings`, { withCredentials: true })
          .catch(() => axios.get(`${API}/public/cms-field-settings`));
        if (alive) setSettings(data?.settings || {});
      } catch {
        if (alive) setSettings({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const shouldShow = (fieldKey, audience = "admin") => {
    const meta = settings[fieldKey];
    if (!meta) return true;   // unknown fields default to visible
    const vis = meta.visible_in || (meta.visible === false ? "hidden" : "admin");
    if (vis === "hidden") return false;
    if (vis === "both") return true;
    return vis === audience;
  };

  return { settings, loading, shouldShow };
}

/**
 * Drop-in wrapper for property-admin (user) forms — hides its children
 * whenever the admin has flipped the field to admin-only or hidden.
 */
export function UserCmsField({ vis, fieldKey, label, required, className, children }) {
  return (
    <CmsFieldWrap
      fieldKey={fieldKey}
      label={label}
      required={required}
      className={className}
      settings={vis?.settings || {}}
      audience="user"
    >
      {children}
    </CmsFieldWrap>
  );
}
